<template>
  <div>
    <div class="options">
      <div
        ref="optionsList"
        class="list gantt_scroll"
        :style="{maxHeight: `${maxHeight}px`}"
      >
        <Container
          drag-handle-selector=".option-drag-handle"
          :lock-axis="'y'"
          :auto-scroll-enabled="true"
          @drop="onDrop(null, optionsItems, $event)"
        >
          <Draggable
            v-for="(item, index) in optionsItems"
            :key="index"
            :class="['list__item', !item.readonly ? 'active' : '', error && index === 0 ? 'is_error' : '']"
          >
            <div
              v-if="optionsItems.length > 1"
              class="dnd_icon option-drag-handle"
            >
              <svg-sprite
                :name="'dragdrop'"
                :size="20"
              />
            </div>
            <div
              :class="['option_input', item.readonly && optionsItems.length > 1 ? 'option-drag-handle' : '']"
              @click="clickEdit(item.id)"
            >
              <vgp-input
                ref="optionInput"
                :data-id="item.id"
                :placeholder="locale('custom_column_item_placeholder')"
                :model-value="item.value"
                :border="false"
                :small="true"
                :change-by-enter="true"
                :use-tab-key="true"
                @onChange="onChangeInput($event, item.id)"
                @update:modelValue="onChangeInput($event, item.id)"
                @onKeyup="addOptionByEnter"
                @onKeydown="onKeydown($event, item)"
                @onBlur="onChangeInput($event, item.id)"
                @onFocus="$emit('resetError')"
              />
            </div>
            <div class="buttons">
              <div
                v-if="optionsItems.length > 1"
                class="buttons__delete"
              >
                <icon-button
                  :icon="{ name:'delete', size: 20 }"
                  size="small"
                  @onClick="deleteOption(item.id)"
                />
              </div>
            </div>
          </Draggable>
        </Container>
      </div>
      <div class="button_add">
        <vgp-button
          class="gp_autotest_columns_bar_add_multiselect_option"
          :label="locale('common_add')"
          type="text"
          :icon="{name: 'plus-1', type: 'bold', size: 20}"
          icon-side="left"
          :x-small="true"
          @onClick="addOption"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Container, Draggable } from 'vue3-smooth-dnd';
import iconButton from '../../../common/VueComponents/globalButton/withIcon/iconButton.vue';
import vgpButton from '../../../common/VueComponents/globalButton/vgpButton.vue';
import svgSprite from '../../../common/VueComponents/VueIcon/svgSprite.vue';

export default {
  name: 'ColumnsBarMultiselectOptions',
  components: {
    vgpButton, iconButton, svgSprite, Container, Draggable,
  },
  props: {
    options: { type: Array, requied: false, default: () => [] },
    error: { type: Boolean, requied: true, default: false },
    maxHeight: { type: Number, required: false, default: 500 },
  },
  emits: {
    onChange: Array,
    resetError: null,
  },
  data() {
    return {
      locale: __,
      optionsItems: [],
      optionIndex: null,
      disabledOptionId: null,
    };
  },
  watch: {
    options: {
      handler(options) {
        if (options && options.length) {
          this.setOptions(options);
        }
      },
      immediate: true,
    },
  },
  created() {
    if (this.options && this.options.length) {
      this.setOptions(this.options);
      this.$emit('onChange', this.optionsItems);
    } else {
      this.addOption(false);
    }
  },
  beforeUnmount() {
    this.optionsItems = [];
  },
  methods: {
    setOptions(options) {
      let newOptionsItems = [];

      if (this.optionsItems.length) {
        const existingOptions = this.optionsItems.filter(opt => options.find(item => item.id === opt.id || opt.newLocalOption));

        existingOptions.forEach(opt => {
          const externalOption = options.find(item => item.id === opt.id);

          if (opt.changedLocalOption || opt.newLocalOption) {
            newOptionsItems.push(opt);
          } else {
            newOptionsItems.push({
              id: externalOption.id,
              value: externalOption.value,
              is_default: externalOption.is_default,
              sort_order: externalOption.sort_order,
              readonly: true,
            });
          }
        });
      } else {
        options.forEach((opt, index) => {
          newOptionsItems.push({
            id: opt.id,
            value: opt.value,
            is_default: opt.is_default,
            sort_order: opt.sort_order,
            readonly: true,
          });
        });
      }

      newOptionsItems.sort((a, b) => a.sort_order - b.sort_order);

      const newOptionsByUser = newOptionsItems.filter(opt => opt.newLocalOption);
      const optionsWithoutUserAction = newOptionsItems.filter(opt => !opt.newLocalOption);

      newOptionsItems = [];
      optionsWithoutUserAction.forEach(opt => {
        const item = {};

        item.id = opt.id;
        item.is_default = opt.is_default;
        item.value = opt.value;
        item.sort_order = opt.sort_order;
        item.readonly = true;

        newOptionsItems.push(item);
      });

      newOptionsByUser.forEach(opt => {
        const preparedUserOption = { ...opt, sort_order: this.optionsItems.length };

        newOptionsItems.push(preparedUserOption);
      });

      this.optionsItems = newOptionsItems;
    },
    deleteOption(id) {
      this.optionsItems = this.optionsItems.filter(opt => opt.id !== id);
      this.$emit('onChange', this.optionsItems);
    },
    addOption(addedByUser = true) {
      const newId = new Date().getTime();

      this.optionsItems.push({
        id: newId,
        is_default: 0,
        value: '',
        sort_order: this.optionsItems.length,
        readonly: true,
        newLocalOption: addedByUser,

      });
      this.$emit('onChange', this.optionsItems);

      setTimeout(() => {
        this.scrollList();
        this.clickEdit(newId);
      }, 0);
    },
    addOptionByEnter(e) {
      if (e.key === 'Enter') {
        this.addOption();
      }
    },
    onDrop(id, optionsItems, dropResult) {
      if (dropResult.removedIndex === null && dropResult.addedIndex === null) return optionsItems;
      if (dropResult.removedIndex === dropResult.addedIndex) return optionsItems;

      const result = [...optionsItems];
      let itemToAdd = null;

      if (dropResult.removedIndex !== null) {
        itemToAdd = result.splice(dropResult.removedIndex, 1)[0];
      }

      if (dropResult.addedIndex !== null) {
        result.splice(dropResult.addedIndex, 0, itemToAdd);
      }

      this.optionsItems = [];

      result.forEach((option, idx) => {
        this.optionsItems.push({ ...option, sort_order: idx });
      });

      this.$emit('onChange', this.optionsItems);
    },
    onKeydown(e, item) {
      if (e.key === 'Tab') {
        const minObject = this.optionsItems.reduce((min, obj) => (obj.sort_order < min.sort_order ? obj : min), this.optionsItems[0]);
        const maxObject = this.optionsItems.reduce((max, obj) => (obj.sort_order > max.sort_order ? obj : max), this.optionsItems[0]);
        const filteredObjects = this.optionsItems.filter(obj => obj.sort_order > item.sort_order);
        const result = filteredObjects.reduce((min, obj) => (obj.sort_order < min.sort_order ? obj : min), filteredObjects[0]);

        this.onChangeInput(e, item.id);
        if (+item.sort_order === +maxObject.sort_order) {
          this.clickEdit(minObject.id);
        } else {
          this.clickEdit(result.id);
        }
        e.preventDefault();
      }
    },
    clickEdit(id) {
      const inputElement = this.$refs.optionInput.find(ref => +ref.$el.dataset.id === +id);

      if (inputElement) {
        inputElement.focusElement();
      }

      this.optionsItems.forEach(opt => {
        if (opt.id === id) {
          opt.readonly = !opt.readonly;
        }
      });
    },
    onChangeInput(input, id) {
      const value = input.target ? input.target.value.trim() : input.trim();
      const currentOption = this.optionsItems.find(opt => opt.id === id);

      if (!currentOption) return;

      const index = this.optionsItems.indexOf(currentOption);

      this.optionsItems.splice(index, 1, {
        ...currentOption,
        value,
        readonly: true,
        changedLocalOption: true,
      });

      this.$emit('onChange', this.optionsItems);
    },
    scrollList() {
      const list = this.$refs.optionsList;

      list.scrollTop = list.scrollHeight;
    },
  },
};
</script>

<style src='./style.less' lang='less' />
